import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { WindowBucket } from "~/features/orders/types-order";
import type { TransferQueueRecord } from "~/features/transfer-queue/types";
import { TransferStatus, TRANSFER_STATUS_LABEL, toTransferStatus } from "~/features/transfer-queue/types";
import { formatDateWithYear, formatWindowRangeAMPM, orDoubleDash, stripCountryCode } from "~/shared/utils/formatting";
import { OutcomeBadge, getOutcomeLabel } from "~/features/orders/components/OutcomeBadge";
import { InlineFilterDropdown } from "~/shared/components/InlineFilterDropdown";
import { GlobalSearchBar } from "~/shared/components/GlobalSearchBar";
import { TransferQueueEditModal } from "./TransferQueueEditModal";

const STATUS_STYLES: Record<TransferStatus, { background: string; color: string }> = {
    [TransferStatus.PENDING]: { background: '#FFF3CD', color: '#856404' },
    [TransferStatus.IN_PROGRESS]: { background: '#D1ECF1', color: '#0C5460' },
    [TransferStatus.COMPLETED]: { background: '#D4EDDA', color: '#155724' },
    [TransferStatus.FAILED]: { background: '#F8D7DA', color: '#721C24' },
    [TransferStatus.CANCELLED]: { background: '#E2E3E5', color: '#383D41' },
    [TransferStatus.CALL_FAILED]: { background: '#F8D7DA', color: '#721C24' },
    [TransferStatus.ARCHIVED]: { background: '#E2E3E5', color: '#383D41' },
};

function StatusBadge({ status }: { status: TransferStatus }) {
    const style = STATUS_STYLES[status] ?? STATUS_STYLES[TransferStatus.PENDING];
    const label = TRANSFER_STATUS_LABEL[status] ?? TRANSFER_STATUS_LABEL[TransferStatus.PENDING];

    return (
        <span
            className="badge"
            style={{
                background: style.background,
                color: style.color,
                padding: '4px 8px',
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 600,
            }}
        >
            {label}
        </span>
    );
}

type TransferQueueTableProps = Readonly<{
    records: TransferQueueRecord[];
    loading?: boolean;
    title?: string;
    emptyMessage?: string;
    onUpdateOrder?: (orderId: string, updates: {
        outcome: string;
        deliveryDate: string;
        window: WindowBucket | null;
        comments: string;
        notes: string;
    }) => void;
    onLockOrder?: (orderId: string) => Promise<boolean>;
    onUnlockOrder?: (orderId: string) => void;
    onStartManualCall?: (orderId: string, phoneNumber: string) => Promise<void>;
    searchQuery?: string;
    onSearchChange?: (value: string) => void;
    onSearchSubmit?: (value: string) => void;
    cityFilter?: string[];
    onCityFilterChange?: (selected: string[]) => void;
    cityOptions?: string[];
    statusFilter?: string[];
    onStatusFilterChange?: (selected: string[]) => void;
    outcomeFilter?: string[];
    onOutcomeFilterChange?: (selected: string[]) => void;
    whiteGloveFilter?: string[];
    onWhiteGloveFilterChange?: (selected: string[]) => void;
}>;

const whiteGloveLabel = (flag?: boolean) => flag ? 'Yes' : 'No';
const rowClass = (flag?: boolean) => flag ? 'whiteglove-yes' : 'whiteglove-no';

function getRecordStatus(record: TransferQueueRecord): TransferStatus {
    return toTransferStatus(record);
}

function getRecordDeliveryDate(record: TransferQueueRecord): string | undefined {
    if (record.updatedDate) {
        return new Date(record.updatedDate).toISOString().split('T')[0];
    }
    if (record.deliveryDateAndWindow?.newDate) {
        return new Date(record.deliveryDateAndWindow.newDate).toISOString().split('T')[0];
    }
    return undefined;
}

function getRecordWindow(
    record: TransferQueueRecord
): { start: number; end: number; startMinutes?: number; endMinutes?: number } | undefined {
    const w = record.updatedWindow ?? record.deliveryDateAndWindow?.window;
    // Keep exact minutes so windows like 9:30 AM - 2:30 PM display correctly
    return w ? { start: w.start, end: w.end, startMinutes: w.startMinutes, endMinutes: w.endMinutes } : undefined;
}

export function TransferQueueTable({
    records,
    loading = false,
    title = "Transfer Queue",
    emptyMessage = "No orders in transfer queue",
    onUpdateOrder,
    onLockOrder,
    onUnlockOrder,
    onStartManualCall,
    searchQuery,
    onSearchChange,
    onSearchSubmit,
    cityFilter = [],
    onCityFilterChange,
    cityOptions,
    statusFilter = [],
    onStatusFilterChange,
    outcomeFilter = [],
    onOutcomeFilterChange,
    whiteGloveFilter = [],
    onWhiteGloveFilterChange,
}: TransferQueueTableProps) {
    const [editingRecord, setEditingRecord] = useState<TransferQueueRecord | null>(null);
    const [editingRowOffset, setEditingRowOffset] = useState(0);
    const [lockingOrderId, setLockingOrderId] = useState<string | null>(null);
    const [confirmCall, setConfirmCall] = useState<{ orderId: string; label: string; number: string } | null>(null);
    const [startingCallForOrderId, setStartingCallForOrderId] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const scrollWrapperRef = useRef<HTMLDivElement>(null);
    const panelRef = useRef<HTMLDivElement>(null);
    const pendingScrollRef = useRef<number | null>(null);

    // Extract unique filter options from all records
    const filterOptions = useMemo(() => {
        const statuses = new Set<string>();
        const outcomes = new Set<string>();
        const whiteGloves = new Set<string>();
        records.forEach(r => {
            const s = TRANSFER_STATUS_LABEL[getRecordStatus(r)];
            if (s) statuses.add(s);
            if (r.outcome) outcomes.add(r.outcome);
            whiteGloves.add(whiteGloveLabel(r.whiteGlove));
        });
        const alpha = (a: string, b: string) => a.localeCompare(b);
        return {
            statuses: Array.from(statuses).sort(alpha),
            outcomes: Array.from(outcomes).sort(alpha),
            cities: cityOptions && cityOptions.length > 0 ? cityOptions : records.map(r => r.city).filter((c): c is string => Boolean(c)).sort(alpha),
            whiteGloves: Array.from(whiteGloves).sort(alpha),
        };
    }, [records, cityOptions]);

    // Apply all column filters client-side; options still derived from all records above
    const displayedRecords = useMemo(() => {
        let result = records;
        if (statusFilter.length > 0)
            result = result.filter(r => {
                const label = TRANSFER_STATUS_LABEL[getRecordStatus(r)];
                return label && statusFilter.includes(label);
            });
        if (outcomeFilter.length > 0)
            result = result.filter(r => r.outcome && outcomeFilter.includes(r.outcome));
        if (whiteGloveFilter.length > 0)
            result = result.filter(r => whiteGloveFilter.includes(whiteGloveLabel(r.whiteGlove)));
        return result;
    }, [records, statusFilter, outcomeFilter, whiteGloveFilter]);

    // Refs to keep the latest values accessible in the cleanup effect
    const editingRecordRef = useRef(editingRecord);
    editingRecordRef.current = editingRecord;
    const onUnlockOrderRef = useRef(onUnlockOrder);
    onUnlockOrderRef.current = onUnlockOrder;

    // Auto-unlock when component unmounts (e.g. user navigates away without Cancel)
    useEffect(() => {
        return () => {
            if (editingRecordRef.current && onUnlockOrderRef.current) {
                onUnlockOrderRef.current(editingRecordRef.current.orderId);
            }
        };
    }, []);

    const handleEdit = async (record: TransferQueueRecord, rowEl: HTMLElement | null) => {
        // If clicking the same order that's already being edited, do nothing
        if (editingRecord?.orderId === record.orderId) return;

        // Unlock the previously selected order before switching
        if (editingRecord && onUnlockOrder) {
            onUnlockOrder(editingRecord.orderId);
        }

        // Save table scroll position before layout shifts
        const savedScrollTop = scrollWrapperRef.current?.scrollTop ?? 0;

        if (onLockOrder) {
            setLockingOrderId(record.orderId);
            try {
                const acquired = await onLockOrder(record.orderId);
                if (!acquired) {
                    // Lock failed — close the panel since we already unlocked the previous order
                    setEditingRecord(null);
                    setEditingRowOffset(0);
                    return;
                }
            } finally {
                setLockingOrderId(null);
            }
        }

        let offset = 0;
        if (rowEl && scrollWrapperRef.current && containerRef.current) {
            const rowRect = rowEl.getBoundingClientRect();
            const scrollRect = scrollWrapperRef.current.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            const titleBarHeight = scrollRect.top - containerRect.top;
            const rowInContent = rowRect.top - scrollRect.top + scrollWrapperRef.current.scrollTop;
            offset = titleBarHeight + rowInContent;
        }

        pendingScrollRef.current = savedScrollTop;
        setEditingRecord(record);
        setEditingRowOffset(offset);
    };

    const handleSave = (updates: {
        outcome: string;
        deliveryDate: string;
        window: WindowBucket | null;
        comments: string;
        notes: string;
    }) => {
        if (editingRecord && onUpdateOrder) {
            onUpdateOrder(editingRecord.orderId, updates);
        }
        if (editingRecord && onUnlockOrder) {
            onUnlockOrder(editingRecord.orderId);
        }
        setEditingRecord(null);
        setEditingRowOffset(0);
    };

    const handleClose = () => {
        if (editingRecord && onUnlockOrder) {
            onUnlockOrder(editingRecord.orderId);
        }
        setEditingRecord(null);
        setEditingRowOffset(0);
    };

    // Scroll the editing row into view after the panel opens
    useLayoutEffect(() => {
        if (!editingRecord || !scrollWrapperRef.current) return;

        const wrapper = scrollWrapperRef.current;
        const row = wrapper.querySelector(
            `tr[data-order-id="${editingRecord.orderId}"]`
        ) as HTMLElement | null;

        if (!row) {
            // Fallback: restore saved scroll position
            if (pendingScrollRef.current != null) {
                wrapper.scrollTop = pendingScrollRef.current;
            }
            pendingScrollRef.current = null;
            return;
        }

        // Position the row just below the sticky header
        const rowRect = row.getBoundingClientRect();
        const wrapperRect = wrapper.getBoundingClientRect();
        const thead = wrapper.querySelector('thead');
        const theadHeight = thead ? thead.getBoundingClientRect().height : 0;
        const rowTopInScroll = rowRect.top - wrapperRect.top + wrapper.scrollTop;

        wrapper.scrollTop = Math.max(0, rowTopInScroll - theadHeight - 8);
        pendingScrollRef.current = null;
    }, [editingRecord]);
    const isEmpty = displayedRecords.length === 0;
    const hasActiveFilter = statusFilter.length > 0 || outcomeFilter.length > 0 || cityFilter.length > 0 || whiteGloveFilter.length > 0;

    return (
        <div
            ref={containerRef}
            style={{
                display: 'flex',
                gap: 10,
                flex: isEmpty && !editingRecord ? '0 0 auto' : 1,
                minHeight: 0,
                width: '100%',
                minWidth: 0,
                alignItems: isEmpty && !editingRecord ? 'flex-start' : 'stretch',
            }}
        >
            <div
                className="table-container table-container--scroll-inner"
                style={{
                    flex: isEmpty ? '0 0 auto' : 1,
                    width: '100%',
                    alignSelf: isEmpty ? 'flex-start' : 'stretch',
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    minWidth: 0,
                    position: 'relative',
                }}
            >
                {loading && records.length > 0 && (
                    <div
                        style={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255,255,255,0.7)',
                            zIndex: 20,
                            borderRadius: 'inherit',
                        }}
                    >
                        <p style={{ color: 'var(--text-muted)', fontSize: 14 }}>Loading…</p>
                    </div>
                )}
                <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', background: 'var(--neutral-gray)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
                    <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600 }}>{title}</h3>
                    <span
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: 'var(--primary-blue)',
                            color: 'white',
                            fontSize: 12,
                            fontWeight: 600,
                        }}
                    >
                        {displayedRecords.length}
                    </span>
                    {onSearchChange && (
                        <div style={{ marginLeft: 'auto' }}>
                            <GlobalSearchBar
                                value={searchQuery ?? ''}
                                onChange={onSearchChange}
                                onSubmit={onSearchSubmit}
                                placeholder="Search by SM#, patient, patient ID, postal, phone…"
                            />
                        </div>
                    )}
                </div>
                <div
                    ref={scrollWrapperRef}
                    className="table-scroll-wrapper"
                    style={{ flex: isEmpty ? '0 0 auto' : 1, minHeight: 0, overflowY: isEmpty ? 'hidden' : 'auto', position: 'relative' }}
                >
                    <table className="table">
                        <thead>
                            <tr>
                                <th>SM Number</th>
                                <th>Patient</th>
                                <th>Patient ID</th>
                                <th>
                                    <InlineFilterDropdown
                                        label="Status"
                                        options={filterOptions.statuses}
                                        selected={statusFilter}
                                        onChange={onStatusFilterChange ?? (() => { })}
                                    />
                                </th>
                                <th>
                                    <InlineFilterDropdown
                                        label="Outcome"
                                        options={filterOptions.outcomes}
                                        selected={outcomeFilter}
                                        onChange={onOutcomeFilterChange ?? (() => { })}
                                        formatOption={getOutcomeLabel}
                                    />
                                </th>
                                <th>Delivery Date & Window</th>
                                <th>
                                    <InlineFilterDropdown
                                        label="City"
                                        options={filterOptions.cities}
                                        selected={cityFilter}
                                        onChange={onCityFilterChange ?? (() => { })}
                                        searchable
                                    />
                                </th>
                                <th>Postal</th>
                                <th>Phone</th>
                                <th>Alt Phone</th>
                                <th>
                                    <InlineFilterDropdown
                                        label="White Glove"
                                        options={filterOptions.whiteGloves}
                                        selected={whiteGloveFilter}
                                        onChange={onWhiteGloveFilterChange ?? (() => { })}
                                    />
                                </th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isEmpty && (
                                <tr>
                                    <td colSpan={12} style={{ textAlign: 'center', padding: 0, color: 'var(--text-muted)', borderBottom: 'none' }}>
                                        <div
                                            style={{
                                                minHeight: 120,
                                                padding: '16px 24px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: 12,
                                            }}
                                        >
                                            <div style={{ fontWeight: 600, fontSize: 14 }}>
                                                {hasActiveFilter ? 'No records match the selected filters' : emptyMessage}
                                            </div>
                                        </div>
                                    </td>
                                </tr>
                            )}
                            {displayedRecords.map(r => {
                                const status = getRecordStatus(r);
                                const deliveryDate = getRecordDeliveryDate(r);
                                const window = getRecordWindow(r);
                                const isEditing = editingRecord?.orderId === r.orderId;
                                const effectivePhone = r.phone ?? r.aiInferredPhone ?? r.rawPhone ?? '';
                                const effectiveAltPhone = r.altPhone ?? r.aiInferredAltPhone ?? r.rawAltPhone ?? '';
                                const phoneDisplay = stripCountryCode(effectivePhone);
                                const altPhoneDisplay = stripCountryCode(effectiveAltPhone);
                                const hasPhone = !!effectivePhone.trim();
                                const hasAltPhone = !!effectiveAltPhone.trim();
                                const canShowCallIcon = status === TransferStatus.FAILED || status === TransferStatus.CALL_FAILED || r.outcome === 'unanswered';

                                return (
                                    <tr
                                        key={r.orderId}
                                        data-order-id={r.orderId}
                                        className={['table-row', rowClass(r.whiteGlove), isEditing ? 'table-row--editing' : ''].filter(Boolean).join(' ')}
                                    >
                                        <td><strong>{r.orderId}</strong></td>
                                        <td>{r.patientName}</td>
                                        <td>{orDoubleDash(r.patientId)}</td>
                                        <td>
                                            <StatusBadge status={status} />
                                        </td>
                                        <td>
                                            {r.outcome ? (
                                                <OutcomeBadge outcome={r.outcome} />
                                            ) : (
                                                <span style={{ color: 'var(--text-muted)' }}>{orDoubleDash(r.outcome)}</span>
                                            )}
                                        </td>
                                        <td>
                                            {deliveryDate ? (
                                                <div style={{ fontWeight: 600 }}>
                                                    <div>{formatDateWithYear(deliveryDate)}</div>
                                                    {window && (
                                                        <div>{formatWindowRangeAMPM(window)}</div>
                                                    )}
                                                </div>
                                            ) : (
                                                orDoubleDash(deliveryDate)
                                            )}
                                        </td>
                                        <td>{orDoubleDash(r.city)}</td>
                                        <td>{orDoubleDash(r.postalCode)}</td>
                                        <td>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                {canShowCallIcon && hasPhone && (
                                                    <button
                                                        type="button"
                                                        title="Call phone number"
                                                        onClick={() => setConfirmCall({ orderId: r.orderId, label: 'Phone', number: effectivePhone })}
                                                        style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 13, cursor: 'pointer', lineHeight: 1 }}
                                                    >
                                                        📞
                                                    </button>
                                                )}
                                                <span>{orDoubleDash(phoneDisplay)}</span>
                                            </span>
                                        </td>
                                        <td>
                                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                {canShowCallIcon && hasAltPhone && (
                                                    <button
                                                        type="button"
                                                        title="Call alternate phone number"
                                                        onClick={() => setConfirmCall({ orderId: r.orderId, label: 'Alt Phone', number: effectiveAltPhone })}
                                                        style={{ background: 'transparent', border: 'none', padding: 0, fontSize: 13, cursor: 'pointer', lineHeight: 1 }}
                                                    >
                                                        📞
                                                    </button>
                                                )}
                                                <span>{orDoubleDash(altPhoneDisplay)}</span>
                                            </span>
                                        </td>
                                        <td>{whiteGloveLabel(r.whiteGlove)}</td>
                                        <td style={{ whiteSpace: 'nowrap' }}>
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                                <button
                                                    title="Edit"
                                                    className={`secondary ${lockingOrderId === r.orderId ? 'btn-locking' : ''}`.trim()}
                                                    onClick={(e) => {
                                                        const row = (e.currentTarget as HTMLElement).closest("tr") as HTMLElement | null;
                                                        handleEdit(r, row);
                                                    }}
                                                    disabled={lockingOrderId === r.orderId}
                                                    style={{ padding: '4px 8px', fontSize: 14 }}
                                                >
                                                    {lockingOrderId === r.orderId ? '⏳' : '✏️'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </div>
            <div
                ref={panelRef}
                style={{
                    width: editingRecord ? '400px' : '0px',
                    flexShrink: 0,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    maxHeight: 'calc(100vh - 260px)',
                }}
            >
                {editingRecord && (
                    <div style={{ width: '400px' }}>
                        <TransferQueueEditModal
                            key={editingRecord.orderId}
                            record={editingRecord}
                            onClose={handleClose}
                            onSave={handleSave}
                        />
                    </div>
                )}
            </div>
            {/* Call confirmation popup */}
            {confirmCall && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 9999,
                    }}
                    onClick={() => setConfirmCall(null)}
                    onKeyDown={(e) => { if (e.key === 'Escape') setConfirmCall(null); }}
                >
                    <div
                        style={{
                            background: 'white',
                            borderRadius: 12,
                            padding: '28px 32px',
                            maxWidth: 400,
                            width: '90%',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
                            textAlign: 'center',
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                    >
                        <div style={{ fontSize: 36, marginBottom: 12 }}>📞</div>
                        <h3 style={{ margin: '0 0 8px', fontSize: 18 }}>Confirm Call</h3>
                        <p style={{ margin: '0 0 24px', color: 'var(--text-muted)', fontSize: 14 }}>
                            Call <strong>{confirmCall.label}</strong> number <strong>{confirmCall.number}</strong>?
                        </p>
                        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
                            <button
                                className="secondary"
                                onClick={() => setConfirmCall(null)}
                                style={{ padding: '8px 20px' }}
                            >
                                Cancel
                            </button>
                            <button
                                className="primary"
                                disabled={startingCallForOrderId === confirmCall.orderId}
                                onClick={async () => {
                                    if (!onStartManualCall) return;
                                    const payload = { ...confirmCall };
                                    setStartingCallForOrderId(payload.orderId);
                                    setConfirmCall(null);
                                    try {
                                        await onStartManualCall(payload.orderId, payload.number);
                                    } finally {
                                        setStartingCallForOrderId(null);
                                    }
                                }}
                                style={{ padding: '8px 20px' }}
                            >
                                {startingCallForOrderId === confirmCall.orderId ? 'Calling...' : 'Call Now'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
